import {useTranslation} from '@/translations/i18n.tsx'
import {Container} from '@components/ui/container'
import {Button} from '@components/ui/button'
import {FC, useState} from 'react'
import {Sheet, SheetContent} from '@components/ui/sheet'
import {PendingActionModalContent} from '@features/home/components/pending-action-modal-content'
import {Skeleton} from '@components/ui/skeleton'
import {useMediaQuery} from '@hooks/use-media-query'

interface RequiredActionsSectionProps {
    asSkeleton?: boolean
    requiredActions: (JSX.Element | null)[]
    isReviewerOnly?: boolean
}

const REQUIRED_CARD_WRAPPER_CLASSES =
    'w-full max-w-[1280px] overflow-x-scroll [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden flex m-0 gap-2 px-4 md:grid md:grid-cols-4 md:grid-rows-2 md:gap-4 md:px-8 md:mx-auto'

const MAX_REQUIRED_ACTIONS_VIEW = 8
export const RequiredActionsSection: FC<RequiredActionsSectionProps> = ({
    asSkeleton,
    requiredActions,
    isReviewerOnly
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)

    const isLarge = useMediaQuery({breakpoint: 'md'})
    if (asSkeleton) {
        return (
            <div className="flex w-full flex-col gap-6">
                <Container>
                    <div className="flex items-center justify-between">
                        <Skeleton className="h-5 w-1/2" />
                        {isLarge && <Skeleton className="h-5 w-28" />}
                    </div>
                </Container>
                <div className={REQUIRED_CARD_WRAPPER_CLASSES}>
                    {Array.from(Array(8)).map((_, index) => (
                        <Skeleton key={index} />
                    ))}
                </div>
                {!isLarge && (
                    <Container>
                        <Skeleton className="h-5 w-28" />
                    </Container>
                )}
            </div>
        )
    }

    return (
        <div className="flex w-full flex-col gap-6">
            <Container>
                <div className="flex items-center justify-between">
                    {!isReviewerOnly && <p>{t('home:complete_the_following_actions')}</p>}
                    {isLarge && requiredActions.length > MAX_REQUIRED_ACTIONS_VIEW && (
                        <Button
                            style={{marginLeft: isReviewerOnly ? 'auto' : undefined}}
                            variant={'tertiary'}
                            onClick={() => setIsOpen(true)}
                        >
                            {t('home:show_all', {counter: requiredActions.length})}
                        </Button>
                    )}
                </div>
            </Container>
            <div className={REQUIRED_CARD_WRAPPER_CLASSES}>{requiredActions.slice(0, MAX_REQUIRED_ACTIONS_VIEW)}</div>
            {!isLarge && requiredActions.length > MAX_REQUIRED_ACTIONS_VIEW && (
                <Container>
                    <Button shape={'text'} variant={'tertiary'} onClick={() => setIsOpen(true)}>
                        {t('home:show_all', {counter: requiredActions.length})}
                    </Button>
                </Container>
            )}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetContent
                    side={isLarge ? 'right' : 'bottom'}
                    className={
                        isLarge
                            ? 'inset-y-8 right-8 grid h-auto w-93.5 grid-rows-[auto_minmax(0,1fr)] rounded-xl sm:max-w-none'
                            : 'grid grid-rows-[auto_minmax(0,1fr)]'
                    }
                >
                    <PendingActionModalContent />
                </SheetContent>
            </Sheet>
        </div>
    )
}
